import type { ReactNode } from 'react';

// ------------------------------------------------------------------
// PageHeader — story 2-127 (UI-DS-00 §3.1 / 2026-08-22-app-shell-ui-
// refinement-design.md §3.1, §4.2-4.3).
// ------------------------------------------------------------------
//
// The two concrete shapes the mapping doc's Focus/Dashboard and Lifecycle/
// Report rows call for (both land on real pages in story 2-130, not here):
//   - Dashboard: title + freshness metadata + a quick-action cluster.
//   - Report detail: title + address metadata + status/version `chips` +
//     actions, PLUS a `toolbar` row (Print/Open) directly under the header.
// One primitive covers both — `toolbar` is the only thing the Report shape
// needs that Dashboard doesn't, and it is optional.
//
// Presentational only (mapping doc §5): `title`/`description` are content,
// `chips`/`actions`/`toolbar` are slots the caller fills with existing DS
// primitives (`Badge`, `Button`, …) — PageHeader never decides what a chip
// says or what an action does.
//
// `headingLevel` defaults to 1 because a REAL page uses `title` as ITS OWN
// `<h1>` (this primitive typically replaces a page's hand-rolled `<h1>`
// block, not sit alongside one) — every consuming page keeps DOD's "exactly
// one real `<h1>`" rule by using PageHeader for that h1, not adding another.
export interface PageHeaderProps {
  title: string;
  /** Supporting line under the title — freshness text ("Updated 2 minutes ago"), an address, template/status summary. */
  description?: ReactNode;
  /** Status chips beside the title (e.g. Approved / v3). Compose `Badge`/`Chip`. */
  chips?: ReactNode;
  /** Primary action cluster, right-aligned. Compose `Button`. */
  actions?: ReactNode;
  /** Optional secondary toolbar row directly under the header (Report detail's Print/Open row, 2026-08-22 spec §4.3). */
  toolbar?: ReactNode;
  headingLevel?: 1 | 2;
  className?: string;
  'data-testid'?: string;
}

export function PageHeader({
  title,
  description,
  chips,
  actions,
  toolbar,
  headingLevel = 1,
  className = '',
  'data-testid': testId,
}: PageHeaderProps): JSX.Element {
  const Heading = headingLevel === 1 ? 'h1' : 'h2';
  return (
    <header className={`space-y-4 ${className}`} data-testid={testId}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <Heading className={headingLevel === 1 ? 'text-2xl font-bold text-foreground' : 'text-xl font-bold text-foreground'}>
              {title}
            </Heading>
            {chips && <div className="flex flex-wrap items-center gap-1.5">{chips}</div>}
          </div>
          {description && <div className="max-w-2xl text-sm text-muted-foreground">{description}</div>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {toolbar && <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">{toolbar}</div>}
    </header>
  );
}
